import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import NewPayment from "@/pages/NewPayment";
import Transactions from "@/pages/Transactions";
import Analytics from "@/pages/Analytics";
import Staff from "@/pages/Staff";
import Settings from "@/pages/Settings";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Receipt from "@/pages/Receipt";
import PaymentTemplates from "@/pages/PaymentTemplates";
import CustomerDisplay from "@/pages/CustomerDisplay";
import OnboardingWizard from "@/pages/OnboardingWizard";
import AdminPanel from "@/pages/AdminPanel";
import FixedQRPay from "@/pages/FixedQRPay";
import Support from "@/pages/Support";
import Reports from "@/pages/Reports";
import ResetPassword from "@/pages/ResetPassword";

function LandingRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.role === 'platform_admin') return <Navigate to="/admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <LandingPage />;
}

function AppRouter() {
  const location = useLocation();

  // Google OAuth callback: redirect with ?code= on /login
  const searchParams = new URLSearchParams(location.search);
  if (searchParams.get('code') && location.pathname === '/login') {
    return <AuthCallback />;
  }

  // Legacy Emergent OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingRoute />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/display/:paymentId" element={<CustomerDisplay />} />
      <Route path="/pay/:storeId/:userId" element={<FixedQRPay />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />

      {/* Protected routes */}
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="payment" element={<NewPayment />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="reports" element={<Reports />} />
        <Route path="staff" element={<Staff />} />
        <Route path="settings" element={<Settings />} />
        <Route path="support" element={<Support />} />
        <Route path="templates" element={<PaymentTemplates />} />
        <Route path="receipt/:transactionId" element={<Receipt />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" richColors closeButton />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
